import React, { useState, useEffect, useCallback, useMemo } from "react";
import useApi from "@/hooks/useApi";

interface ActivityEntry {
  id: number;
  siteId: number;
  category: string;
  message: string;
  details?: string;
  source?: string;
  timestamp: string;
}

interface ApiResponse {
  entries?: ActivityEntry[];
  totalCount?: number;
  page?: number;
  totalPages?: number;
}

type SortKey = "timestamp" | "category" | "source";
type SortDir = "asc" | "desc";

const CATEGORY_BADGES: Record<string, { bg: string; text: string; label: string }> = {
  limit_reached: { bg: "bg-sweet-orange-50", text: "text-sweet-orange-700", label: "Limit Reached" },
  field_auto_created: { bg: "bg-green-100", text: "text-green-700", label: "Auto-Created" },
  error: { bg: "bg-red-100", text: "text-red-700", label: "Error" },
};

const SOURCE_BADGES: Record<string, { bg: string; text: string; label: string }> = {
  embed: { bg: "bg-teal-50", text: "text-teal-700", label: "Embed" },
  workflow: { bg: "bg-purple-50", text: "text-purple-700", label: "Workflow" },
  survey: { bg: "bg-sweet-orange-50", text: "text-sweet-orange-700", label: "Survey" },
  api: { bg: "bg-slate-100", text: "text-slate-600", label: "API" },
  admin: { bg: "bg-slate-100", text: "text-slate-600", label: "Admin" },
};

export default function AdminActivityLogPanel() {
  const api = useApi();

  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("timestamp");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filterCategory, setFilterCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const fetchData = useCallback(async () => {
    try {
      const res = await api.messaging.get("AdminActivityLog");
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data: ApiResponse = await res.json();
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
    } catch {
      // safe default
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredEntries = useMemo(() => {
    let result = [...entries];

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (e) =>
          e.message.toLowerCase().includes(lower) ||
          (e.details ?? "").toLowerCase().includes(lower) ||
          (e.source ?? "").toLowerCase().includes(lower) ||
          e.category.toLowerCase().includes(lower)
      );
    }

    if (filterCategory !== "all") {
      result = result.filter((e) => e.category === filterCategory);
    }

    result.sort((a, b) => {
      let aVal: string | number = a[sortKey] ?? "";
      let bVal: string | number = b[sortKey] ?? "";
      if (sortKey === "timestamp") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [entries, search, filterCategory, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const pagedEntries = filteredEntries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " \u2191" : " \u2193") : "";

  function renderCategoryBadge(category: string) {
    const badge = CATEGORY_BADGES[category] ?? { bg: "bg-slate-100", text: "text-slate-700", label: category };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  }

  function renderSourceBadge(source?: string) {
    const key = source || "admin";
    const badge = SOURCE_BADGES[key] ?? SOURCE_BADGES.admin;
    return (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <h2 className="text-xl font-semibold mb-1">Activity Log</h2>
      <p className="text-sm text-slate-500 mb-4">
        Tracks auto-created fields, subscription limit events, and system errors.
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Search messages, details..."
          className="px-3 py-1.5 border border-slate-300 rounded text-sm w-64 focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          aria-label="Search activity log"
        />
        <label htmlFor="filterCategory" className="sr-only">Filter by category</label>
        <select
          id="filterCategory"
          className="px-3 py-1.5 border border-slate-300 rounded text-sm focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Categories</option>
          <option value="limit_reached">Limit Reached</option>
          <option value="field_auto_created">Auto-Created</option>
          <option value="error">Error</option>
        </select>
        <button
          className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
          onClick={() => { setSearch(""); setFilterCategory("all"); setCurrentPage(1); }}
        >
          Clear Filters
        </button>
        <span className="text-xs text-slate-400 ml-auto">
          {filteredEntries.length} of {entries.length} entries
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("timestamp")}>
                Date/Time{sortArrow("timestamp")}
              </th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("category")}>
                Category{sortArrow("category")}
              </th>
              <th className="px-3 py-2">Message</th>
              <th className="px-3 py-2">Details</th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("source")}>
                Source{sortArrow("source")}
              </th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Loading...</td></tr>
            ) : pagedEntries.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No activity entries found</td></tr>
            ) : (
              pagedEntries.map((entry) => (
                <tr key={entry.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                    {new Date(entry.timestamp).toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5">{renderCategoryBadge(entry.category)}</td>
                  <td className="px-3 py-1.5 text-xs max-w-[300px] truncate" title={entry.message}>
                    {entry.message}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-500 max-w-[250px] truncate" title={entry.details ?? ""}>
                    {entry.details || "\u2014"}
                  </td>
                  <td className="px-3 py-1.5">{renderSourceBadge(entry.source ?? undefined)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
          <span>Page {currentPage} of {totalPages}</span>
          <div className="flex gap-1">
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
}
